import { useRef } from 'react'
import {
  Collapse,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  Divider,
  Skeleton,
  Box,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useJsApiLoader } from '@react-google-maps/api'

import { CollapsedItem } from './collapsed-item.component'
import { MediaList } from './media.component'
import { ConsumerScanMap } from './map.component'
import { ImpactsList } from './impacts.component'
import { NotRecognizedImpactsList } from './not-recognized-impacts.component'
import { TransactionItem } from './transaction-item.component'

import { capitalizeFirstLetter, getScanExplorerTxUrl } from 'src/common/utils'
import { jsApiLoaderOptions } from 'src/constants'
import { ETranslationNs } from 'src/types'
import { EScanNetwork, IConsumerScanRespDto } from 'src/types'
import { useStyles } from './../../styles'
import { useGetConsumerScan } from 'src/hooks'
import { TextTag } from 'src/components/text-tag'
import { UtcAndLocalDatesCell } from 'src/components/utc-and-local-dates-cell'

interface IProps {
  collapsed: boolean
  item: IConsumerScanRespDto
  userTz?: string
  isHighlighted?: boolean
}

export const BodyRowCollapse = ({ collapsed, item, userTz, isHighlighted }: IProps) => {
  const [t] = useTranslation([ETranslationNs.ScanForm, ETranslationNs.Scans, ETranslationNs.Common])
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'))

  const mapRef = useRef<google.maps.Map | null>(null)

  const { data, isLoading } = useGetConsumerScan(item.id, collapsed)

  const { isLoaded } = useJsApiLoader(jsApiLoaderOptions)

  const getScannerLocation = (lat: number, lng: number) => ({
    lat,
    lng,
  })

  return (
    <TableRow
      sx={{
        maxWidth: '1024px',
        backgroundColor: isHighlighted
          ? theme.palette.custom.lightGreen
          : theme.palette.custom.white,
      }}
    >
      <TableCell colSpan={9} className={classes.cell}>
        <Collapse in={collapsed} timeout="auto" unmountOnExit>
          <ul className={classes.collapsedContent}>
            <CollapsedItem
              title={t('scan-to-earn-mgmt-barcode-id-label.v', { ns: ETranslationNs.Common })}
              node={<Typography sx={{ fontSize: '14px' }}>{item.barcode_id}</Typography>}
            />
            <CollapsedItem
              title={t('scan-to-earn-mgmt-company-name-label.v', { ns: ETranslationNs.Common })}
              node={<Typography sx={{ fontSize: '14px' }}>{item.company_name}</Typography>}
            />
            <CollapsedItem
              title={t('scan-to-earn-mgmt-product-name-label.v', { ns: ETranslationNs.Common })}
              node={<Typography sx={{ fontSize: '14px' }}>{item.product_name}</Typography>}
            />

            <CollapsedItem
              title={t('scan-to-earn-mgmt-product-type-label.v', { ns: ETranslationNs.Common })}
              node={
                !isLoading ? (
                  <Typography sx={{ fontSize: '14px' }}>
                    {capitalizeFirstLetter(data?.product_type || '')}
                  </Typography>
                ) : (
                  <Skeleton
                    width={'120px'}
                    height={'21px'}
                    variant="rectangular"
                    sx={{ borderRadius: '10px' }}
                  />
                )
              }
            />

            <CollapsedItem
              title={t('scan-to-earn-mgmt-scanner-country-label.v', { ns: ETranslationNs.Common })}
              node={
                !isLoading ? (
                  <Typography sx={{ fontSize: '14px' }}>
                    {data?.scanner_info?.country || '-'}
                  </Typography>
                ) : (
                  <Skeleton
                    width={'120px'}
                    height={'21px'}
                    variant="rectangular"
                    sx={{ borderRadius: '10px' }}
                  />
                )
              }
            />

            <CollapsedItem
              title={t('scan-to-earn-mgmt-package-language-label.v', { ns: ETranslationNs.Common })}
              node={
                !isLoading ? (
                  <Typography sx={{ fontSize: '14px' }}>
                    {data?.scanner_info?.package_language || '-'}
                  </Typography>
                ) : (
                  <Skeleton
                    width={'120px'}
                    height={'21px'}
                    variant="rectangular"
                    sx={{ borderRadius: '10px' }}
                  />
                )
              }
            />

            <CollapsedItem
              title={'Challenges'}
              node={
                !isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexWrap: 'wrap',
                      maxWidth: '100%',
                    }}
                  >
                    {item?.rewardActivity?.payload?.challenges &&
                    item.rewardActivity.payload.challenges.length > 0
                      ? item.rewardActivity.payload.challenges.map((item) => (
                          <TextTag
                            label={item.name}
                            sx={{ wordBreak: 'break-all', maxWidth: '140px' }}
                            bgColor={theme.palette.custom.grey}
                            color={theme.palette.custom.darkBlue2}
                          />
                        ))
                      : '-'}
                  </Box>
                ) : (
                  <Skeleton
                    width={'120px'}
                    height={'21px'}
                    variant="rectangular"
                    sx={{ borderRadius: '10px' }}
                  />
                )
              }
            />

            <CollapsedItem
              title={t('scan-to-earn-mgmt-summary-label.v', { ns: ETranslationNs.Common })}
              node={
                !isLoading ? (
                  <Typography sx={{ fontSize: '14px', maxWidth: '700px', width: '100%' }}>
                    {data?.summary || ''}
                  </Typography>
                ) : (
                  <Box sx={{ maxWidth: '700px', width: '100%' }}>
                    <Skeleton
                      width={'100%'}
                      height={'80px'}
                      variant="rectangular"
                      sx={{ borderRadius: '10px' }}
                    />
                  </Box>
                )
              }
              direction="column"
              sx={{ marginTop: '10px' }}
            />

            {data?.media && data.media.length > 0 && (
              <CollapsedItem
                title={t('scan-to-earn-mgmt-media-label.v', { ns: ETranslationNs.Common })}
                node={<MediaList media={data.media} />}
                direction="column"
                sx={{ marginTop: '10px', marginBottom: '10px' }}
              />
            )}

            {data?.impactLibraries && (
              <CollapsedItem
                title={t('scan-to-earn-mgmt-impacts-label.v', { ns: ETranslationNs.Common })}
                node={<ImpactsList impacts={data.impactLibraries} />}
                direction="column"
                sx={{ marginTop: '10px' }}
              />
            )}

            {data?.not_recognized_impacts && (
              <CollapsedItem
                title={t('scan-to-earn-mgmt-not-recognized-impacts-label.v', {
                  ns: ETranslationNs.Common,
                })}
                node={<NotRecognizedImpactsList newImpacts={data.not_recognized_impacts} />}
                direction="column"
                sx={{ marginTop: '10px', marginBottom: '20px' }}
              />
            )}

            {data?.scanner_info?.coords?.latitude &&
              data?.scanner_info?.coords?.longitude &&
              isLoaded && (
                <ConsumerScanMap
                  scannerLocation={getScannerLocation(
                    data.scanner_info.coords.latitude,
                    data.scanner_info.coords.longitude,
                  )}
                  center={getScannerLocation(
                    data.scanner_info.coords.latitude,
                    data.scanner_info.coords.longitude,
                  )}
                  scannerName={item.user.name || ''}
                  mapRef={mapRef}
                />
              )}

            {(data?.user_tx || data?.user_tx_decrypted) && (
              <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', my: '5px' }} />
            )}

            {data?.user_tx && (
              <CollapsedItem
                title={t('user-tx-hash-label.v', { ns: ETranslationNs.Scans })}
                node={
                  <TransactionItem
                    to={data.user_tx && getScanExplorerTxUrl(data.user_tx, EScanNetwork.BASE)}
                    text={data.user_tx || '-'}
                  />
                }
              />
            )}

            {data?.user_tx_decrypted && (
              <CollapsedItem
                title={t('user-tx-hash-decrypted-label.v')}
                node={
                  <TransactionItem
                    to={
                      data.user_tx_decrypted &&
                      getScanExplorerTxUrl(data.user_tx_decrypted, EScanNetwork.BASE)
                    }
                    text={data.user_tx_decrypted || '-'}
                  />
                }
              />
            )}

            {(data?.user_tx || data?.user_tx_decrypted) &&
              (data?.approved_by || data?.not_approved_by) && (
                <Divider sx={{ borderStyle: 'dashed', borderWidth: '1px', my: '5px' }} />
              )}

            {data?.approved_by && (
              <CollapsedItem
                title={t('scan-history-approved-by-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {data.approved_by}
                  </Typography>
                }
              />
            )}
            {data?.approved_on && (
              <CollapsedItem
                title={t('table-reviewed-at-column.v', { ns: ETranslationNs.Scans })}
                node={
                  // <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                  //   {getTableTimestamp({ date: data.approved_on, timezone: userTz })}
                  // </Typography>
                  <UtcAndLocalDatesCell
                    date={data.approved_on}
                    loalTimezone={userTz}
                    localDateFontSize={isMobile ? '12px' : '14px'}
                    utcDateFontSize={isMobile ? '11px' : '13px'}
                    containerSx={{ alignItems: 'start' }}
                  />
                }
              />
            )}

            {data?.not_approved_by && (
              <CollapsedItem
                title={t('scan-not-approved-by-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <Typography sx={{ color: theme.palette.custom.grey2, fontSize: '14px' }}>
                    {data.not_approved_by}
                  </Typography>
                }
              />
            )}
            {data?.not_approved_on && (
              <CollapsedItem
                title={t('scan-not-approved-at-lable.v', { ns: ETranslationNs.Scans })}
                node={
                  <UtcAndLocalDatesCell
                    date={data.not_approved_on}
                    loalTimezone={userTz}
                    localDateFontSize={isMobile ? '12px' : '14px'}
                    utcDateFontSize={isMobile ? '11px' : '13px'}
                    containerSx={{ alignItems: 'start' }}
                  />
                }
              />
            )}
          </ul>
        </Collapse>
      </TableCell>
    </TableRow>
  )
}
